import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import authorisationsHelper from "../../../../API/Authorisations/authorisationsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import AuthorisationsSettingsAI from "./AuthorisationsSettingsAI";

const HASH_ROUTED_SECTIONS = ["profile", "credentials", "oauth", "usage"];

const DEFAULT_AUTHORISATION_VALUES = {
  name: "",
  description: "",
  scope: "",
  grantType: "",
  clientId: "",
  clientSecret: "",
  authorizationBaseUrl: "",
  tokenBaseUrl: "",
  type: "apikey",
};

const AuthorisationBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const authorisationId = params?.authorisationId;
  const isEditMode = !!authorisationId && authorisationId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isAuthorisationsLoading, setIsAuthorisationsLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedAuthorisation = useMemo(
    () => state?.authorisations?.find((u) => u?.id === authorisationId),
    [state?.authorisations, authorisationId]
  );

  const loadAuthorisations = useCallback(
    async (workspaceID) => {
      try {
        setIsAuthorisationsLoading(true);
        const authorisationsData = await authorisationsHelper.getAuthorisations(
          workspaceID,
          null
        );
        const authorisations =
          authorisationsData?.data?.authorisationsByWorkspace?.items?.filter(
            (u) => u?._deleted !== true
          ) || [];
        dispatch("SET_AUTHORISATIONS", authorisations);
      } catch (error) {
        console.error(error);
      } finally {
        setIsAuthorisationsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const authorisations = state?.authorisations || [];
    const belongsToWorkspace = authorisations.some(
      (u) => u?.workspaceID === wsId
    );
    if (authorisations.length === 0 || !belongsToWorkspace) {
      loadAuthorisations(wsId);
    } else {
      setIsAuthorisationsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!authorisationId) {
      history.replace(`/workspaces/${workspaceId}/settings/authorisations`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorisationId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (authorisationId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues({ ...DEFAULT_AUTHORISATION_VALUES });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorisationId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isAuthorisationsLoading) return;
    if (editInitRef.current === authorisationId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const authorisation = (state?.authorisations || []).find(
      (u) => u?.id === authorisationId
    );
    if (!authorisation) {
      const listIsStale = state?.authorisations?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(
        `/workspaces/${activeWorkspaceId}/settings/authorisations`
      );
      return;
    }
    editInitRef.current = authorisationId;
    setInitialMapValues({ ...authorisation });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authorisationId,
    isEditMode,
    isAuthorisationsLoading,
    state?.authorisations,
    getActiveWorkspace()?.id,
  ]);

  const isSectionVisible = (sectionId) => {
    switch (sectionId) {
      case "usage":
        return isEditMode;
      default:
        return true;
    }
  };

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("authorisations.builderGroupSetup", {
          defaultValue: "Set up",
        }),
        items: [
          {
            id: "profile",
            title: t("authorisations.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "credentials",
            title: t("authorisations.builderSectionCredentials", {
              defaultValue: "Credentials",
            }),
            icon: AppAssets.icons.credentials,
          },
          {
            id: "oauth",
            title: t("authorisations.builderSectionOauth", {
              defaultValue: "OAuth",
            }),
            icon: AppAssets.icons.lock,
          },
        ],
      },
      {
        id: "system",
        label: t("authorisations.builderGroupSystem", {
          defaultValue: "System",
        }),
        items: [
          {
            id: "usage",
            title: t("authorisations.builderSectionUsage", {
              defaultValue: "Usage",
            }),
            icon: AppAssets.icons.taskAgent,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, t]);

  useEffect(() => {
    if (!isSectionVisible(activeSection)) {
      setActiveSection("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMapValues]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw) && isSectionVisible(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorisationId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/authorisations/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("authorisations.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isAuthorisationsLoading &&
    (!isEditMode || initialMapValues?.id === authorisationId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleAuthorisationCreated = (status, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("authorisationCreationSuccessCaption"), {
        type: "success",
      });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEditMode && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/authorisations/${savedId}`);
      }
    } else {
      AppToasty.show(t("authorisationCreationFailedCaption"), {
        type: "danger",
      });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingAuthorisations")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <AuthorisationsSettingsAI
            key={authorisationId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? authorisationId : undefined}
            opt={isEditMode ? selectedAuthorisation : undefined}
            settingType="authorisation"
            onAuthCreated={handleAuthorisationCreated}
            onAuthorisationDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/authorisations`)
            }
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/authorisations`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.authorisations")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || selectedAuthorisation?.name || ""
            : t("authorisations.builderNewAuthorisation", {
                defaultValue: "New authorisation",
              })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("authorisations.unsavedChangesSubCaption", {
          defaultValue:
            "This authorisation has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default AuthorisationBuilderScreenContainer;
